"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.PUSHPIN_MODIFIED_EVENT = exports.PUSHPIN_CREATED_EVENT = void 0;
exports.createIssuePushpinDraft = createIssuePushpinDraft;
exports.unmountPushpin = exports.transformIssuesToPushPinData = exports.mountPushpins = exports.mountPushpin = exports.isPushpinRendered = exports.hasLinkedDocument = exports.getViewableDetailsByViewerInstance = exports.getPushpinStatus = exports.getLinkedDocument = exports.getBubbleNodeByCriterion = exports.createSinglePushpin = void 0;
var _sdk = require("../../../sdk");
var _urnUtils = require("./urnUtils");
var _DeriveViewableInformationFromPushPinData = require("../../PushpinsToolbar/DeriveViewableInformationFromPushPinData");
var _uuid = require("uuid");
const PUSHPIN_CREATED_EVENT = exports.PUSHPIN_CREATED_EVENT = "pushpin.created";
const PUSHPIN_MODIFIED_EVENT = exports.PUSHPIN_MODIFIED_EVENT = "pushpin.modified";
const hasLinkedDocument = issue => issue.linkedDocuments && issue.linkedDocuments.length > 0;
exports.hasLinkedDocument = hasLinkedDocument;
const getLinkedDocument = issue => issue.linkedDocuments[0];
exports.getLinkedDocument = getLinkedDocument;
const isPushpinRendered = (pushPinExtension, issueId) => pushPinExtension && pushPinExtension.pushPinManager && pushPinExtension.pushPinManager.getItemById(issueId);

/**
 * Unmounts an Issue push pin
 */
exports.isPushpinRendered = isPushpinRendered;
const unmountPushpin = (pushPinExtension, _ref) => {
  let {
    id
  } = _ref;
  if (!pushPinExtension) {
    return;
  }
  if (isPushpinRendered(pushPinExtension, id)) {
    pushPinExtension.removeItemById(id);
  }
};
exports.unmountPushpin = unmountPushpin;
function getModelNode(model) {
  return model.getData().loadOptions.bubbleNode;
}
const getModelUrn = model => {
  const node = getModelNode(model);
  const root = node.getRootNode();
  const urn = root.urn();
  return (0, _urnUtils.decodeUrn)(urn);
};
const isModelVisibleForLineageUrn = (viewer, lineageUrn) => {
  const visibleModels = viewer.getVisibleModels();
  return visibleModels.some(visibleModel => {
    const visibleModelUrn = getModelUrn(visibleModel);
    const visibleModelLineageUrnAndVersion = (0, _urnUtils.getLineageUrnAndVersion)(visibleModelUrn);
    return visibleModelLineageUrnAndVersion && visibleModelLineageUrnAndVersion.lineageUrn === lineageUrn;
  });
};
const isModelForPushpinVisible = (pushPinExtension, issue) => {
  return issue.linkedDocuments.some(linkedDocument => isModelVisibleForLineageUrn(pushPinExtension.viewer, linkedDocument.urn));
};
const transformIssuesToPushPinData = (pushPinExtension, issues) => {
  if (!pushPinExtension) {
    return [];
  }
  return issues.filter(issue => {
    // If there is no visible model for the issue, the pushpin cannot be positioned correctly
    if (!isModelForPushpinVisible(pushPinExtension, issue)) {
      return false;
    }
    if (!pushPinExtension || !pushPinExtension.pushPinManager || isPushpinRendered(pushPinExtension, issue.id)) {
      return false;
    }
    if (!hasLinkedDocument(issue)) {
      return false;
    }
    return true;
  }).map(issue => {
    const {
      details: {
        position,
        objectId,
        viewerState,
        externalId,
        attributesVersion
      }
    } = getLinkedDocument(issue);
    const pushPinData = {
      id: issue.id,
      label: issue.displayId,
      status: getPushpinStatus(issue.status),
      position: {
        x: position.x,
        y: position.y,
        z: position.z
      },
      type: "issues",
      objectId,
      viewerState,
      externalId,
      attributesVersion
    };
    return pushPinData;
  });
};

/**
 * Mounts an Issue push pin
 */
exports.transformIssuesToPushPinData = transformIssuesToPushPinData;
const mountPushpin = (pushPinExtension, issue) => {
  const [pushPinData] = transformIssuesToPushPinData(pushPinExtension, [issue]);
  if (pushPinData) {
    pushPinExtension === null || pushPinExtension === void 0 || pushPinExtension.loadItemsV2(pushPinData);
    pushPinExtension === null || pushPinExtension === void 0 || pushPinExtension.setVisibleById(issue.id, true);
  }
};

/**
 * Mounts an Issue push pin
 */
exports.mountPushpin = mountPushpin;
const mountPushpins = (pushPinExtension, issues) => {
  const pushPins = transformIssuesToPushPinData(pushPinExtension, issues);
  pushPinExtension === null || pushPinExtension === void 0 || pushPinExtension.loadItemsV2(pushPins);
  issues.forEach(issue => {
    pushPinExtension === null || pushPinExtension === void 0 || pushPinExtension.setVisibleById(issue.id, true);
  });
};

// This string structure must be preserved! it is reliant on the pushpinExtension format, that exists in the firefly.js repo (LMV).
// This string should be in the same string format as seen here, under the ACC-issues section:
// https://git.autodesk.com/A360/firefly.js/blob/develop/extensions/Pushpins/PushPinConstants.js
exports.mountPushpins = mountPushpins;
const getPushpinStatus = status => {
  if (!status) {
    return "acc-issues-open";
  }
  return "acc-issues-".concat(status);
};
exports.getPushpinStatus = getPushpinStatus;
const createSinglePushpin = (pushPinExtension, _ref2) => {
  let {
    label,
    status,
    type
  } = _ref2;
  return new Promise((resolve, reject) => {
    if (!pushPinExtension) {
      reject(new Error("The given pushPinExtension is not defined"));
    }
    const handlePushpinCreated = event => {
      pushPinExtension.endCreateItem();
      pushPinExtension.removeEventListener(PUSHPIN_CREATED_EVENT, handlePushpinCreated);
      resolve(event);
    };
    pushPinExtension.startCreateItem({
      label,
      status: getPushpinStatus(status),
      type
    });
    pushPinExtension.addEventListener(PUSHPIN_CREATED_EVENT, handlePushpinCreated);
  });
};
exports.createSinglePushpin = createSinglePushpin;
function createLinkedDocumentDraft(lineageUrn, data, versionNumber) {
  const linkedDocument = {
    urn: lineageUrn,
    createdAtVersion: versionNumber,
    type: data.position.z ? "TwoDVectorPushpin" : "TwoDRasterPushpin",
    details: {
      position: data.position,
      objectId: data.objectId,
      viewerState: data.viewerState,
      externalId: data.externalId
    }
  };
  if (data.attributesVersion) {
    Object.assign(linkedDocument.details.viewerState, {
      attributesVersion: data.attributesVersion
    });
  }
  if (data.objectData) {
    linkedDocument.details.viewable = {
      viewableId: data.objectData.viewableId,
      guid: data.objectData.guid,
      name: data.objectData.viewName,
      is3D: true
    };
  }
  return linkedDocument;
}
function createIssuePushpinDraft(event, pushPinExtension) {
  var _event$value, _issueType$title, _issueType$id;
  let {
    issueType,
    viewableInformation
  } = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  let data = event === null || event === void 0 || (_event$value = event.value) === null || _event$value === void 0 ? void 0 : _event$value.data;

  // In aggregated view case retrieve viewable information pushpin
  const issueViewableInformation = viewableInformation !== null && viewableInformation !== void 0 ? viewableInformation : (0, _DeriveViewableInformationFromPushPinData.deriveViewableInformationFromPushPinData)(data);
  if (!issueViewableInformation) {
    return;
  }
  const {
    lineageUrn,
    versionNumber
  } = issueViewableInformation;

  // Required in order to align pushpin positioning values
  if (pushPinExtension !== null && pushPinExtension !== void 0 && pushPinExtension.getLegacyPushPinData) {
    data = pushPinExtension.getLegacyPushPinData(event === null || event === void 0 ? void 0 : event.value);
  }
  const linkedDocument = createLinkedDocumentDraft(lineageUrn, data, versionNumber);
  return {
    id: (0, _uuid.v4)(),
    title: (_issueType$title = issueType === null || issueType === void 0 ? void 0 : issueType.title) !== null && _issueType$title !== void 0 ? _issueType$title : "",
    issueTypeId: (_issueType$id = issueType === null || issueType === void 0 ? void 0 : issueType.id) !== null && _issueType$id !== void 0 ? _issueType$id : "",
    status: _sdk.IssueStatus.Open,
    linkedDocuments: [linkedDocument],
    published: true
  };
}
const getBubbleNodeByCriterion = (viewerInstance, criterion) => {
  return viewerInstance.model.getDocumentNode().getRootNode().search(criterion)[0];
};
exports.getBubbleNodeByCriterion = getBubbleNodeByCriterion;
const getViewableDetailsByViewerInstance = (viewerInstance, viewableCriterion) => {
  if (!viewerInstance.model) {
    return null;
  }
  let bubbleNode = getBubbleNodeByCriterion(viewerInstance, {
    guid: viewableCriterion.guid
  });
  if (!bubbleNode) {
    bubbleNode = getBubbleNodeByCriterion(viewerInstance, {
      viewableID: viewableCriterion.viewableId
    });
  }
  if (!bubbleNode) {
    bubbleNode = getBubbleNodeByCriterion(viewerInstance, {
      name: viewableCriterion.name
    });
  }
  return bubbleNode ? {
    viewableId: bubbleNode.data.viewableID,
    guid: bubbleNode.data.guid,
    name: bubbleNode.data.name
  } : null;
};
exports.getViewableDetailsByViewerInstance = getViewableDetailsByViewerInstance;
//# sourceMappingURL=pushpinHelpers.js.map